import React, { useMemo, useState } from "react";
import { Search, ChevronDown, ChevronUp, GripVertical, CalendarCheck } from "lucide-react";

const STATUS_STYLE = {
  ELIGIBLE: "bg-emerald-50 border-emerald-200 text-emerald-700",
  WARNING: "bg-amber-50 border-amber-200 text-amber-700",
  BLOCKED: "bg-red-50 border-red-200 text-red-700",
};
const FILTERS = [
  { key: "ALL", label: "All" }, { key: "ELIGIBLE", label: "Available" },
  { key: "WARNING", label: "Warning" }, { key: "BLOCKED", label: "Blocked" },
  { key: "UNDER_SCHEDULED", label: "Under 20h" },
];
const UNDER_SCHEDULED_MINUTES = 20 * 60;

/** Left pane of the 3-pane Schedule Builder workspace. Clicking a card SELECTS/HIGHLIGHTS the
 * employee (adds them to the center grid + highlights their row) - it never creates an
 * assignment by itself. Dragging a card onto a ScheduleGrid cell is the progressive-enhancement
 * path straight into Quick Assign; the checkbox controls whether the employee currently takes up
 * a row in the (necessarily narrower) center grid. */
export const EligibilityEmployeeList = ({ board, checkedIds, highlightedId, onToggleChecked, onSelectEmployee }) => {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [positionFilter, setPositionFilter] = useState("ALL");
  const [expandedId, setExpandedId] = useState(null);

  const positions = useMemo(() => {
    const set = new Set((board?.employees || []).map((e) => e.position).filter(Boolean));
    return Array.from(set).sort();
  }, [board]);

  const employees = useMemo(() => {
    const list = board?.employees || [];
    return list.filter((e) => {
      if (filter === "UNDER_SCHEDULED" && e.scheduled_minutes_in_period >= UNDER_SCHEDULED_MINUTES) return false;
      if (filter !== "ALL" && filter !== "UNDER_SCHEDULED" && e.eligibility_status !== filter) return false;
      if (positionFilter !== "ALL" && e.position !== positionFilter) return false;
      if (search && !e.legal_name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [board, search, filter, positionFilter]);

  return (
    <div className="flex flex-col h-full" data-testid="schedule-eligibility-pane">
      <div className="p-3 border-b border-gray-100 space-y-2">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Employees &amp; Eligibility</p>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-gray-500" />
          <input
            data-testid="eligibility-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employees"
            className="w-full pl-8 pr-2 py-1.5 rounded-lg border border-gray-300 text-xs"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`eligibility-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${filter === f.key ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {positions.length > 1 && (
          <select data-testid="eligibility-position-filter" value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)} className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-xs">
            <option value="ALL">All positions</option>
            {positions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-gray-100" data-testid="eligibility-employee-list">
        {employees.map((e) => (
          <div key={e.employee_id} className={`p-2.5 ${highlightedId === e.employee_id ? "bg-blue-50" : ""}`} data-testid={`eligibility-card-${e.employee_id}`}>
            <div
              draggable
              onDragStart={(ev) => ev.dataTransfer.setData("text/employee-id", e.employee_id)}
              onClick={() => onSelectEmployee(e.employee_id)}
              data-testid={`eligibility-card-select-${e.employee_id}`}
              className="flex items-center gap-2 cursor-pointer group"
              title="Click to select/highlight, or drag onto a date to assign"
            >
              <input
                type="checkbox"
                data-testid={`eligibility-card-checkbox-${e.employee_id}`}
                checked={checkedIds.has(e.employee_id)}
                onClick={(ev) => ev.stopPropagation()}
                onChange={() => onToggleChecked(e.employee_id)}
                className="shrink-0"
                title="Show/hide this employee's row in the schedule grid"
              />
              <GripVertical size={13} className="text-gray-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-blue-600">{e.legal_name}</p>
                <p className="text-[11px] text-gray-500 truncate flex items-center gap-1">
                  {e.position || "No position set"} - {Math.round(e.scheduled_minutes_in_period / 60)}h scheduled
                  {e.availability_notes_count > 0 && <CalendarCheck size={10} className="text-emerald-500 shrink-0" title="Availability submitted" />}
                </p>
              </div>
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_STYLE[e.eligibility_status]}`} data-testid={`eligibility-status-${e.employee_id}`}>
                {e.eligibility_status}
              </span>
              {e.reasons?.length > 0 && (
                <button
                  data-testid={`eligibility-expand-${e.employee_id}`}
                  onClick={(ev) => { ev.stopPropagation(); setExpandedId(expandedId === e.employee_id ? null : e.employee_id); }}
                  className="shrink-0 text-gray-300 hover:text-gray-600"
                  title="Why can't I assign this employee?"
                >
                  {expandedId === e.employee_id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              )}
            </div>
            {expandedId === e.employee_id && (
              <div className="mt-1.5 ml-6 space-y-1" data-testid={`eligibility-reasons-${e.employee_id}`}>
                {e.reasons.map((r, i) => (
                  <p key={i} className={`text-[11px] ${r.severity === "BLOCKING" ? "text-red-600" : r.severity === "WARNING" ? "text-amber-600" : "text-gray-500"}`}>
                    <span className="font-bold">{r.severity}:</span> {r.message}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
        {employees.length === 0 && <p className="text-xs text-gray-500 text-center py-6">No employees match this filter.</p>}
      </div>
    </div>
  );
};
